import { useState, useEffect, useRef } from "react";
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
  Paper,
  CircularProgress
} from '@mui/material';

export default function CompactTestimonialLeadForm({
    title="Get salary optimization tips"
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [token, setToken] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);

  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    // Skip Turnstile in development mode to avoid domain validation errors
    if (process.env.NODE_ENV === 'development') {
      setIsFormReady(true);
      return;
    }

    if (!TURNSTILE_SITE_KEY) {
      setIsFormReady(true);
      return;
    }

    // Set a timeout to show form even if Turnstile fails to load
    const formReadyTimeout = setTimeout(() => {
      setIsFormReady(true);
    }, 2000);

    const renderTurnstile = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current) return;
      
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "auto",
          callback: (token) => {
            setToken(token);
            setIsFormReady(true);
            clearTimeout(formReadyTimeout);
          },
          "expired-callback": () => setToken(""),
          "error-callback": () => {
            setToken("");
            setIsFormReady(true);
            clearTimeout(formReadyTimeout);
          }
        });
        // Set form ready even if callback doesn't fire immediately
        setTimeout(() => {
          setIsFormReady(true);
          clearTimeout(formReadyTimeout);
        }, 500);
      } catch (error) {
        setIsFormReady(true);
        clearTimeout(formReadyTimeout);
      }
    };

    // Add a small delay to ensure DOM is ready
    const initTurnstile = () => {
      if (window.turnstile) {
        // Small delay to ensure containerRef is mounted
        setTimeout(renderTurnstile, 100);
      } else {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          // Small delay after script loads to ensure DOM is ready
          setTimeout(renderTurnstile, 100);
        };
        document.head.appendChild(script);
      }
    };

    // Use requestAnimationFrame to ensure DOM is painted
    requestAnimationFrame(initTurnstile);

    return () => {
      clearTimeout(formReadyTimeout);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        } catch (error) {
          // Silently handle cleanup errors
        }
      }
    };
  }, [TURNSTILE_SITE_KEY]);

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("loading");
    setMsg("");

    if (!email.trim()) {
      setStatus("error");
      setMsg("Please enter your email address.");
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setStatus("error");
      setMsg("Please enter a valid email address.");
      return;
    }

    if (!gdprConsent) {
      setStatus("error");
      setMsg("Please consent to receiving emails.");
      return;
    }

    // Check Turnstile in production
    if (process.env.NODE_ENV === 'production' && TURNSTILE_SITE_KEY && !token) {
      setStatus("error");
      setMsg("Please complete the verification.");
      return;
    }

    const payload = {
      email: email,
      turnstileToken: token,
      website: "", // honeypot
      gdprConsent: gdprConsent,
      gdprTimestamp: new Date().toISOString(),
      source: 'compact-testimonial-leadform'
    };

    try {
      // In development, simulate successful submission
      if (process.env.NODE_ENV === 'development') {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
        setStatus("success");
        setMsg("Development mode: Form submitted successfully!");
        setEmail("");
        setGdprConsent(false);
        return;
      }

      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setMsg(data.message || "Thanks! Check your email.");
        setEmail("");
        setGdprConsent(false);
        
        // Reset Turnstile
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
          setToken("");
        }
      } else {
        setStatus("error");
        setMsg(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setStatus("error");
      setMsg("Network error. Please try again.");
    }
  }

  return (
    <Paper
      elevation={8}
      sx={{
        background: 'linear-gradient(135deg, #e91e63 0%, #9c27b0 100%)',
        color: 'white',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -10,
          right: -10,
          width: 40,
          height: 40,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -15,
          left: -15,
          width: 50,
          height: 50,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '50%',
        }}
      />

      <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        {/* Hidden Turnstile container - always available for ref */}
        {TURNSTILE_SITE_KEY && process.env.NODE_ENV !== 'development' && (
          <div ref={containerRef} style={{ display: isFormReady ? 'block' : 'none', textAlign: 'center', marginBottom: '1rem' }} />
        )}

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
            textAlign: 'center',
            fontSize: '1.1rem',
            lineHeight: 1.2
          }}
        >
          {title}
        </Typography>

        {!isFormReady ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
            <CircularProgress sx={{ color: 'white' }} size={24} />
          </Box>
        ) : (
          /* Form */
          <form onSubmit={onSubmit} noValidate>
          <TextField
            fullWidth
            type="email"
            placeholder="your@email.com"
            variant="outlined"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={status === "loading"}
            error={status === "error" && !email.trim()}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderRadius: 2,
                fontSize: '0.9rem',
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255,255,255,0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
                '& input::placeholder': {
                  color: 'rgba(0,0,0,0.5)',
                  opacity: 1,
                }
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                disabled={status === "loading"}
                size="small"
                sx={{
                  color: 'white',
                  '&.Mui-checked': {
                    color: 'white',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: 18,
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                  }
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ 
                fontSize: '0.78rem', 
                opacity: 1,
                color: 'white',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                lineHeight: 1.3
              }}>
                I agree to receive helpful tips.{' '}
                <Link 
                  href="/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ 
                    color: 'white', 
                    textDecoration: 'underline',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'none'
                    }
                  }}
                >
                  Privacy
                </Link>
              </Typography>
            }
            sx={{ mb: 1.5, alignItems: 'flex-start' }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={status === "loading"}
            sx={{
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 700,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.9)',
              color: '#e91e63',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              textTransform: 'none',
              '&:hover': {
                background: 'white',
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
              },
              '&:disabled': {
                background: 'rgba(255,255,255,0.5)',
                color: 'rgba(233, 30, 99, 0.5)',
              },
            }}
          >
{status === "loading" ? "Sending..." : "Get tips"}
          </Button>

          {/* Status Messages */}
          {msg && (
            <Alert
              severity={status === "success" ? "success" : "error"}
              sx={{ 
                mt: 1.5,
                borderRadius: 2,
                fontSize: '0.8rem',
                backgroundColor: status === "success" ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)',
                color: 'white',
                '& .MuiAlert-message': {
                  fontSize: '0.8rem'
                },
                '& .MuiAlert-icon': {
                  color: 'white'
                }
              }}
            >
              {msg}
            </Alert>
          )}
        </form>
        )}
      </Box>
    </Paper>
  );
}